/**
 * Tactical-board on-disk schema. Wraps the canonical
 * `SharedShape` model in `./shared-shape.ts` with tactical-only
 * extensions (player + ball tokens, scene metadata, game format,
 * formation) and exposes scene-level validation that calls
 * `validateSharedShape` per shape.
 *
 * Naming policy on the tactical board: the on-disk field names
 * already match the canonical in-memory names (`kind`,
 * `stroke_width`). The only adapter work here is converting the
 * canonical camelCase `strokeWidth` ↔ snake_case `stroke_width`
 * at the I/O boundary. Everything else is identity.
 *
 * Source-of-truth references in models.py:
 *   - `validate_tactical_board_payload` (orchestrator)
 *   - `_validate_board_shape` (per-shape; subset of SharedShape)
 *   - `_validate_board_token` (player/ball)
 *   - `_VALID_BOARD_TOKEN_KINDS`, `_VALID_PITCH_KINDS`,
 *     `_VALID_BOARD_ORIENTATIONS`, `_VALID_BOARD_GAME_FORMATS`
 */

import { clamp01 } from "./coords";
import type { GameFormat } from "./formations";
import {
  type SharedColor,
  type SharedShape,
  type ValidationResult,
  clampSharedShape,
  normalizeSharedColor,
  validateSharedShape,
} from "./shared-shape";

// ---------------------------------------------------------------------------
// Tactical-board-only constants
// ---------------------------------------------------------------------------

export const VALID_TOKEN_KINDS = ["player", "ball"] as const;
export type TokenKind = (typeof VALID_TOKEN_KINDS)[number];

export const VALID_PITCH_KINDS = ["soccer_full"] as const;
export type PitchKind = (typeof VALID_PITCH_KINDS)[number];

export const VALID_BOARD_ORIENTATIONS = ["landscape"] as const;
export type BoardOrientation = (typeof VALID_BOARD_ORIENTATIONS)[number];

export const VALID_GAME_FORMATS = ["7v7", "9v9", "11v11"] as const;

export const MAX_TOKENS = 40;
export const MAX_SHAPES = 40;
export const MAX_TOKEN_LABEL_LENGTH = 24;
export const MAX_FORMATION_LENGTH = 32;

// ---------------------------------------------------------------------------
// Token + scene shapes
// ---------------------------------------------------------------------------

export type BoardToken = {
  kind: TokenKind;
  id?: string;
  x: number;
  y: number;
  label?: string;
  player_id?: string;
  /** Optional swatch color, from the shared closed palette. Set on
   * player tokens so a coach can place e.g. blue vs red players;
   * absent ⇒ the renderer's default fill. Off-palette / absent
   * values normalize away (the field is simply dropped). */
  color?: SharedColor;
};

/** Tactical-board shapes are the canonical `SharedShape` set. The
 * union alias is re-exported here so existing imports of `BoardShape`
 * keep working — and so reviewers see at a glance that the tactical
 * board does NOT layer anything onto the shared shape set. */
export type BoardShape = SharedShape;

/** Re-export of the discriminated sub-types for cases where the
 * caller wants narrow access (e.g. building a zone-specific
 * Transformer attachment). */
export type {
  SharedArrowShape as BoardArrow,
  SharedFreehandShape as BoardFreehand,
  SharedLabelShape as BoardLabel,
  SharedLineShape as BoardLine,
  SharedZoneShape as BoardZone,
} from "./shared-shape";

export type BoardScene = {
  version: 1;
  pitch_kind: PitchKind;
  orientation: BoardOrientation;
  game_format?: GameFormat;
  formation?: string;
  tokens: BoardToken[];
  shapes: BoardShape[];
};

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

export function isTokenKind(value: unknown): value is TokenKind {
  return typeof value === "string" && (VALID_TOKEN_KINDS as readonly string[]).includes(value);
}

/** Validate a tactical-only token (player / ball). Shared shapes go
 * through `validateSharedShape` in `./shared-shape.ts`. */
export function validateToken(
  input: unknown,
  index = 0,
): ValidationResult<BoardToken> {
  if (typeof input !== "object" || input === null) {
    return { ok: false, reason: `tokens[${index}] must be an object` };
  }
  const t = input as Record<string, unknown>;
  if (!isTokenKind(t.kind)) {
    return {
      ok: false,
      reason: `tokens[${index}].kind must be one of ${VALID_TOKEN_KINDS.join(", ")}`,
    };
  }
  if (
    typeof t.x !== "number" ||
    !Number.isFinite(t.x) ||
    t.x < 0 ||
    t.x > 1 ||
    typeof t.y !== "number" ||
    !Number.isFinite(t.y) ||
    t.y < 0 ||
    t.y > 1
  ) {
    return { ok: false, reason: `tokens[${index}] x/y must be in [0, 1]` };
  }
  const label = typeof t.label === "string" ? t.label : "";
  if (label.length > MAX_TOKEN_LABEL_LENGTH) {
    return {
      ok: false,
      reason: `tokens[${index}] label exceeds max length ${MAX_TOKEN_LABEL_LENGTH}`,
    };
  }
  // Off-palette / absent color → field dropped (not a hard error),
  // mirroring the backend's defensive `_normalize_board_color`.
  const color = normalizeSharedColor(t.color);
  return {
    ok: true,
    value: {
      kind: t.kind as TokenKind,
      x: t.x,
      y: t.y,
      label,
      player_id: typeof t.player_id === "string" ? t.player_id : "",
      ...(typeof t.id === "string" && t.id ? { id: t.id } : {}),
      ...(color ? { color } : {}),
    },
  };
}

/** Validate a single tactical-board shape. This is a thin wrapper
 * around `validateSharedShape` — the tactical board accepts exactly
 * the shared shape set. Kept as a named export so callers don't have
 * to know about the shared module. */
export function validateShape(
  input: unknown,
  index = 0,
): ValidationResult<BoardShape> {
  return validateSharedShape(input, index);
}

/** Validate an entire tactical-board scene. Strict — mirrors the
 * backend's `validate_tactical_board_payload`. Used at the save
 * boundary AND at load.
 *
 * Renderer-safe fallback is the caller's job: on a `{ok: false}`
 * result, the caller decides whether to surface the error or silently
 * fall back to `emptyScene()`. The renderer (`TacticalBoardView`)
 * itself only consumes already-validated scenes, never raw input.
 *
 * Behavior:
 *   - null / undefined ROOT → empty scene (the whole blob being null
 *     mirrors the backend's "no tactical_board_json at all" case)
 *   - non-object → error
 *   - missing tokens / shapes key → defaults to [] (matches the
 *     backend's `value["tokens"] if "tokens" in value else []`)
 *   - explicit null tokens / shapes → ERROR. The backend uses
 *     dict-key lookup, so an explicit JSON null becomes Python None
 *     and `isinstance(None, list)` is False, raising 422. The client
 *     mirrors that strictly. (PR #6 round-2 review-fix.)
 *   - non-list tokens / shapes (object, string, number, boolean) →
 *     error (the original PR #6 fix; the pre-PR version silently
 *     coerced everything-not-array to [])
 *   - unknown pitch_kind / orientation / game_format → error
 *   - per-item validation failures bubble up via the shared validator
 */
export function validateScene(input: unknown): ValidationResult<BoardScene> {
  if (input === null || input === undefined) {
    return { ok: true, value: emptyScene() };
  }
  if (typeof input !== "object") {
    return { ok: false, reason: "tactical_board_json must be an object" };
  }
  const s = input as Record<string, unknown>;
  const version = s.version === undefined ? 1 : s.version;
  if (version !== 1) {
    return { ok: false, reason: "tactical_board_json.version must be 1" };
  }
  const pitch_kind = s.pitch_kind ?? "soccer_full";
  if (!(VALID_PITCH_KINDS as readonly string[]).includes(pitch_kind as string)) {
    return { ok: false, reason: "pitch_kind must be soccer_full (only supported kind)" };
  }
  const orientation = s.orientation ?? "landscape";
  if (
    !(VALID_BOARD_ORIENTATIONS as readonly string[]).includes(orientation as string)
  ) {
    return { ok: false, reason: "orientation must be landscape" };
  }

  // Mirror the backend's strict array boundary
  // (`validate_tactical_board_payload` in models.py:
  //   `tokens_raw = value["tokens"] if "tokens" in value else []`
  //   `if not isinstance(tokens_raw, list): raise`).
  //
  // The MISSING-vs-NULL distinction matters:
  //   - missing key → defaults to [] (legacy clients that never
  //     emitted tokens / shapes — and the renderer-safe "absent
  //     means empty" convention).
  //   - explicit JSON `null` → Python sees `None`, isinstance(None,
  //     list) is False, backend raises. The client MUST also reject
  //     explicit null so a corrupted save doesn't sneak past us and
  //     get a 422 at the server. (PR #6 round-2 review-fix.)
  //   - anything else (object, string, number, boolean) → reject.
  let tokensRaw: unknown[];
  if (!("tokens" in s)) {
    tokensRaw = [];
  } else if (Array.isArray(s.tokens)) {
    tokensRaw = s.tokens;
  } else {
    return { ok: false, reason: "tokens must be a list" };
  }
  if (tokensRaw.length > MAX_TOKENS) {
    return { ok: false, reason: `tokens exceed max ${MAX_TOKENS}` };
  }
  const tokens: BoardToken[] = [];
  for (let i = 0; i < tokensRaw.length; i++) {
    const r = validateToken(tokensRaw[i], i);
    if (!r.ok) return r;
    tokens.push(r.value);
  }

  let shapesRaw: unknown[];
  if (!("shapes" in s)) {
    shapesRaw = [];
  } else if (Array.isArray(s.shapes)) {
    shapesRaw = s.shapes;
  } else {
    return { ok: false, reason: "shapes must be a list" };
  }
  if (shapesRaw.length > MAX_SHAPES) {
    return { ok: false, reason: `shapes exceed max ${MAX_SHAPES}` };
  }
  const shapes: BoardShape[] = [];
  for (let i = 0; i < shapesRaw.length; i++) {
    const r = validateShape(shapesRaw[i], i);
    if (!r.ok) return r;
    shapes.push(r.value);
  }

  let game_format: GameFormat | undefined;
  if (s.game_format !== undefined && s.game_format !== null) {
    if (!(VALID_GAME_FORMATS as readonly string[]).includes(s.game_format as string)) {
      return { ok: false, reason: "game_format must be one of 7v7 / 9v9 / 11v11" };
    }
    game_format = s.game_format as GameFormat;
  }

  let formation: string | undefined;
  if (s.formation !== undefined && s.formation !== null) {
    if (
      typeof s.formation !== "string" ||
      !s.formation ||
      s.formation.length > MAX_FORMATION_LENGTH
    ) {
      return {
        ok: false,
        reason: `formation must be a non-empty string under ${MAX_FORMATION_LENGTH} chars`,
      };
    }
    formation = s.formation;
  }

  return {
    ok: true,
    value: {
      version: 1,
      pitch_kind: "soccer_full",
      orientation: "landscape",
      ...(game_format !== undefined ? { game_format } : {}),
      ...(formation !== undefined ? { formation } : {}),
      tokens,
      shapes,
    },
  };
}

export function emptyScene(): BoardScene {
  return {
    version: 1,
    pitch_kind: "soccer_full",
    orientation: "landscape",
    tokens: [],
    shapes: [],
  };
}

/** Clamp any tactical-board shape into [0, 1]. Defers to
 * `clampSharedShape` for the shape geometry. */
export function clampShape(shape: BoardShape): BoardShape {
  return clampSharedShape(shape);
}

/** Clamp a token's anchor point. */
export function clampToken(token: BoardToken): BoardToken {
  return { ...token, x: clamp01(token.x), y: clamp01(token.y) };
}

// ---------------------------------------------------------------------------
// On-disk adapters
// ---------------------------------------------------------------------------
//
// The tactical board persists `kind` (camelCase identical) and
// `stroke_width` (snake_case). The canonical in-memory shape uses
// `strokeWidth`. These adapters translate at the I/O boundary so
// components/hooks/store never see the on-disk form.
//
// The tokens / scene wrapper don't need renaming — they already match.

type OnDiskShape = Omit<SharedShape, "strokeWidth"> & { stroke_width?: number };

/** Convert an in-memory `SharedShape` to the on-disk tactical-board
 * shape with `stroke_width`. Strips the canonical `strokeWidth`
 * field. */
export function shapeToOnDisk(shape: SharedShape): OnDiskShape {
  const { strokeWidth, ...rest } = shape;
  const out = rest as OnDiskShape;
  if (strokeWidth !== undefined) out.stroke_width = strokeWidth;
  return out;
}

/** Convert an on-disk tactical-board shape (with `stroke_width`)
 * back to the canonical in-memory shape. */
export function shapeFromOnDisk(shape: unknown): SharedShape | null {
  if (typeof shape !== "object" || shape === null) return null;
  const s = { ...(shape as Record<string, unknown>) };
  if ("stroke_width" in s) {
    s.strokeWidth = s.stroke_width;
    delete s.stroke_width;
  }
  const r = validateSharedShape(s);
  return r.ok ? r.value : null;
}

/** Convert an in-memory scene to the on-disk JSON the backend
 * stores in `tactical_board_json`. Shapes go through the adapter so
 * `strokeWidth` becomes `stroke_width`. Tokens + scene wrapper are
 * identity. */
export function sceneToOnDisk(scene: BoardScene): {
  version: 1;
  pitch_kind: PitchKind;
  orientation: BoardOrientation;
  game_format?: GameFormat;
  formation?: string;
  tokens: BoardToken[];
  shapes: OnDiskShape[];
} {
  return {
    version: 1,
    pitch_kind: scene.pitch_kind,
    orientation: scene.orientation,
    ...(scene.game_format ? { game_format: scene.game_format } : {}),
    ...(scene.formation ? { formation: scene.formation } : {}),
    tokens: scene.tokens,
    shapes: scene.shapes.map(shapeToOnDisk),
  };
}

/** Parse a wire-format `tactical_board_json` blob into a validated
 * in-memory scene. Pre-translates `stroke_width` → `strokeWidth` on
 * each shape before delegating to `validateScene`. */
export function sceneFromOnDisk(input: unknown): ValidationResult<BoardScene> {
  if (typeof input !== "object" || input === null) {
    return validateScene(input);
  }
  const s = { ...(input as Record<string, unknown>) };
  if (Array.isArray(s.shapes)) {
    s.shapes = (s.shapes as unknown[]).map((raw) => {
      if (typeof raw !== "object" || raw === null) return raw;
      const copy: Record<string, unknown> = { ...(raw as Record<string, unknown>) };
      if ("stroke_width" in copy) {
        copy.strokeWidth = copy.stroke_width;
        delete copy.stroke_width;
      }
      return copy;
    });
  }
  return validateScene(s);
}

// Re-export the shared predicate + boundary validators with the
// tactical-board aliases used by callers + tests.
export {
  isSharedColor as isValidColor,
  isSharedShapeKind as isShapeKind,
  isSharedStrokeWidth as isValidStrokeWidth,
  isValidUnit,
  normalizeSharedColor as normalizeColor,
  SHARED_MAX_LABEL_LENGTH as MAX_LABEL_LENGTH,
  SHARED_MAX_FREEHAND_POINTS as MAX_FREEHAND_POINTS,
  SHARED_MIN_FREEHAND_POINTS as MIN_FREEHAND_POINTS,
  SHARED_MIN_ZONE_SIZE as MIN_ZONE_SIZE,
  SHARED_STROKE_WIDTH_DEFAULT as BOARD_STROKE_WIDTH_DEFAULT,
  SHARED_STROKE_WIDTH_MAX as BOARD_STROKE_WIDTH_MAX,
  SHARED_STROKE_WIDTH_MIN as BOARD_STROKE_WIDTH_MIN,
} from "./shared-shape";

import { normalizeSharedStrokeWidth, SHARED_STROKE_WIDTH_DEFAULT } from "./shared-shape";

/** Tactical-board variant of stroke-width normalization: falls
 * through to the default integer (3) for invalid / undefined input
 * rather than returning `undefined`. The shared boundary primitive
 * returns undefined; the tactical board (which has always rendered
 * a default-3 stroke for legacy shapes that lack the field) wraps
 * it with the fallback. */
export function normalizeStrokeWidth(value: unknown): number {
  return normalizeSharedStrokeWidth(value) ?? SHARED_STROKE_WIDTH_DEFAULT;
}

/** Re-export the canonical color type with the tactical-board name. */
export type { SharedColor as BoardColor } from "./shared-shape";

// Backward-compat re-exports for `VALID_*` consts that previously
// lived in this file. The shared module owns palette + kinds; this
// file owns tactical-only sets (tokens, pitch kinds, orientations).
export {
  ALL_VALID_COLORS as VALID_COLORS,
  SHARED_SHAPE_KINDS as VALID_SHAPE_KINDS,
} from "./shared-shape";
